import { useDispatch, useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectBillingSubscriptionData,
  selectCustomerSubscriptionData,
  selectElectricityMeterSubscriptionData,
  selectEnergyCharacteristicsSubscriptionData,
  selectEnergyContractSubscriptionData,
  selectGasMeterSubscriptionData,
  selectMainAddressSubscriptionData,
  selectTelecomContractSubscriptionData,
} from '../SubscriptionFormSelectors'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import {
  selectBankingModuleConfigurationData,
  selectCommonFormConfigurationData,
  selectCustomerModuleConfigurationData,
  selectProviderConfiguration,
} from '../SubscriptionFormConfigurationSelectors'
import { updateAdditionalSalesData, updateCustomerData } from '../SubscriptionFormSlice'
import {
  addCommonFormErrors,
  deleteCommonFormErrors,
  updateCustomerModuleConfiguration,
} from '@/containers/SubscriptionFormPage/SubscriptionFormConfigurationSlice'
import { subscriptionFormActions } from '@/containers/SubscriptionFormPage/SubscriptionFormSagas'
import useBusinessType from '@/shared/hooks/useBusinessType'
import { t } from 'i18next'

const useAdditionalSaleFormData = () => {
  const dispatch = useDispatch()
  const {
    zohoId,
    name,
    surname,
    civility,
    phone1,
    phone2,
    email,
    birthdate,
    pro_social_reason: proSocialReason,
    pro_siret: proSiret,
  } = useSelector(selectCustomerSubscriptionData)

  const { iban, bic } = useSelector(selectBillingSubscriptionData)
  const { isMcpAgent } = useSelector(selectAuthData)
  const { streetNumber, city, zipcode, address, inseeCode, housingType, tenantType, floor } = useSelector(
    selectMainAddressSubscriptionData,
  )
  const { pceNo: pce } = useSelector(selectGasMeterSubscriptionData)
  const { pdlNo: pdl } = useSelector(selectElectricityMeterSubscriptionData)
  const { siretIsLoading, siretIsValid } = useSelector(selectCustomerModuleConfigurationData)
  const { prestationType } = useSelector(selectEnergyCharacteristicsSubscriptionData)

  const { providerId } = useSelector(selectTelecomContractSubscriptionData)
  const { name: providerName } = useSelector(selectProviderConfiguration)
  const { offerId } = useSelector(selectEnergyContractSubscriptionData)

  const { type: businessType } = useBusinessType({
    loaderRoutesEnergy: {},
    loaderRoutesInsurance: {},
    loaderRoutes: {},
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })

  const removeErrors = error => {
    dispatch(deleteCommonFormErrors(error))
  }

  const customerUpdate = payload => {
    dispatch(updateCustomerData(payload))
  }

  const handleSiretResponse = response => {
    if (!response.valid) {
      dispatch(
        addCommonFormErrors({
          siret: [t('subscriptionForm:customer_module.proSiret.error')],
        }),
      )
      customerUpdate({
        pro_ape_code: '',
        pro_social_reason: '',
      })
      dispatch(updateCustomerModuleConfiguration({ siretIsValid: false }))
      return
    }

    customerUpdate({
      pro_ape_code: response.apeCode,
      pro_social_reason: response.socialReason,
    })
    dispatch(updateCustomerModuleConfiguration({ siretIsValid: true }))
  }

  const verifySiret = () => {
    dispatch({
      type: subscriptionFormActions.CUSTOMER_MODULE_VERIFY_SIRET,
      siret: proSiret,
      callback: handleSiretResponse,
    })
  }

  const {
    verify_iban_url: verifyIbanUrl,
    current_subscriber: subscription,
    assistanceProducts: assistanceList,
    axa_email_url: axaEmailUrl,
    additional_sales_url: saveLeadUrl,
    interactions_url: interactionsUrl,
    assurancePro: assuranceProList,
    verisureTelecom: verisureTelecomList,
    telecomSat: telecomSatList,
    telecomEnergy: telecomEnergyList,
    protected: protectedList,
    protectedClubProducts: protectedClubList,
    accessoriesSfr: accessoriesSfrList,
    carbonOffsetProducts: carbonOffsetList,
    assuranceProducts: assuranceList,
    assuranceDefaultProducts: assuranceDefaultList,
    assuranceEmprunteurProducts: assuranceEmprunteurList,
    assuranceEmprunteurMeilleurTauxProducts: assuranceEmprunteurMeilleurTauxList,
    offshoreTelecom: offshoreTelecomList,
    partnership: partnershipList,
    energyProPlaceDesEnergies: energyProPlaceDesEnergiesList,
    bankPanorabanques: bankPanorabanquesList,
    bankinAssuranceProducts: bankinAssuranceList,
    telecomProducts: telecomList,
    telecomLovysProducts: telecomLovysList,
    movingNextoriesProducts: nextoriesList,
    monConseillerPersoProducts: monConseillerPersoList,
    boilerGarankaProducts: boilerGarankaList,
    boilerProxiserveProducts: boilerProxiserveList,
    verisure: verisureList,
    groupPurchaseTelecom: groupPurchaseTelecomList,
    assistancePro: assistanceProList,
    floraAssurance: floraAssuranceList,
    yagoAssurance: yagoAssuranceList,
    santevetAssurance: santevetAssuranceList,
    ethias: ethiasList,
    eliteSaleProduct: eliteList,
    engieBrusselsProduct: engieBrusselsList,
    verisureTelecomBe: verisureTelecomBeList,
    boilerCoverEneco: boilerCoverEnecoList,
    boilerCoverEngie: boilerCoverEngieList,
    boilerCoverHomeserve: boilerCoverHomeserveList,
    administrativeService: mesAllocsList,
    tucoenergie: tucoenergieList,
    energygo: energygoList,
    youStock: youStockList,
    eddyProprietaire: eddyProprietaireList,
    voltalisBizdev: voltalisBizdevList,
    neat: neatList,
    century21: century21List,
    axaSmartphoneProducts: axaSmartphoneList,
    additionalSalesStatuses,
    country,
    garanka_encrypt: garankaEncryptUrl,
    neat_resend_email_url: neatResendEmailUrl,
    voltalis_appointment_url: voltalisAppointmentUrl,
    voltalis_status_url: voltalisStatusUrl,
    voltalis_customer_call_url: voltalisCustomerCallUrl,
    get_cache_url: additionalSaleCheckCacheUrl,
    homyosProducts: homyosList,
    mutuelleSantianeProducts: mutuelleSantianeList,
    yoloProducts: yoloList,
    irenProducts: irenList,
    timProducts: timList,
    fastWebProducts: fastWebList,
    protectedItProducts: protectedItList,
    edisonProducts: edisonList,
    enelProducts: enelList,
    sorgeniaProducts: sorgeniaList,
  } = useSelector(selectPageConfigurationData)

  const { ibanValidationData } = useSelector(selectBankingModuleConfigurationData)

  const isAlreadyMonConseillerPerso =
    !!subscription?.sale_mon_conseiller_perso && subscription.sale_mon_conseiller_perso.status_code !== 'abandon'
  const { token, isEditable, mandatoryFields, errors } = useSelector(selectCommonFormConfigurationData)
  const agentEmail = subscription?.created_by?.email ?? ''

  const updateLead = (data, name) => {
    dispatch(updateAdditionalSalesData({ data, name }))
  }

  return {
    zohoId,
    name,
    surname,
    civility,
    phone1,
    phone2,
    email,
    birthdate,
    proSocialReason,
    proSiret,
    pdl,
    pce,
    prestationType,
    iban,
    bic,
    streetNumber,
    city,
    zipcode,
    address,
    inseeCode,
    agentEmail,
    housingType,
    tenantType,
    providerId,
    providerName,
    offerId,
    country,
    token,
    isEditable,
    ibanValidationData,
    businessType,
    mandatoryFields,
    errors,
    removeErrors,
    additionalSalesStatuses,
    siretIsLoading,
    siretIsValid,
    verifySiret,
    updateLead,
    floor,
    urls: {
      verifyIban: verifyIbanUrl,
      saveLeadUrl,
      interactionsUrl,
      garankaEncryptUrl,
      neatResendEmailUrl,
      voltalisAppointmentUrl,
      voltalisStatusUrl,
      voltalisCustomerCallUrl,
      additionalSaleCheckCacheUrl,
    },
    showOnlyAllowLeadMcp: !isMcpAgent && isAlreadyMonConseillerPerso,
    haveAlreadyAnyLead:
      !!subscription?.sale_telecom_energy ||
      !!subscription?.sale_telecom ||
      !!subscription?.sale_verisure_telecom ||
      !!subscription?.sale_assurance_default ||
      !!subscription?.sale_assurance_emprunteur,
    sales: {
      leadAssistance: {
        list: assistanceList ?? [],
        saleDatabaseData: subscription?.sale_assistance_id,
        axaEmailUrl,
      },
      leadVerisureTelecom: {
        list: verisureTelecomList ?? [],
        saleDatabaseData: subscription?.sale_verisure_telecom,
      },
      leadTelecomSat: {
        list: telecomSatList ?? [],
        saleDatabaseData: subscription?.sale_telecom_sat,
      },
      leadTelecomEnergy: {
        list: telecomEnergyList ?? [],
        saleDatabaseData: subscription?.sale_telecom_energy,
      },
      leadProtected: {
        list: protectedList ?? [],
        saleDatabaseData: subscription?.sale_protected,
      },
      leadProtectedClub: {
        list: protectedClubList ?? [],
        saleDatabaseData: subscription?.sale_protected_club,
      },
      leadCarbonOffset: {
        list: carbonOffsetList ?? [],
        saleDatabaseData: subscription?.sale_carbon_offset,
      },
      leadAssurance: {
        list: assuranceList ?? [],
        saleDatabaseData: subscription?.sale_assurance,
      },
      leadAssuranceDefault: {
        list: assuranceDefaultList ?? [],
        saleDatabaseData: subscription?.sale_assurance_default,
      },
      leadAssuranceEmprunteur: {
        list: assuranceEmprunteurList ?? [],
        saleDatabaseData: subscription?.sale_assurance_emprunteur,
      },
      leadAssuranceEmprunteurMeilleurTaux: {
        list: assuranceEmprunteurMeilleurTauxList ?? [],
        saleDatabaseData: subscription?.sale_assurance_emprunteur_meilleur_taux,
      },
      offshoreTelecom: {
        list: offshoreTelecomList ?? [],
        saleDatabaseData: subscription?.sale_offshore_telecom,
      },
      partnership: {
        list: partnershipList ?? [],
        saleDatabaseData: subscription?.sale_partnership,
      },
      leadEnergyProPlaceDesEnergies: {
        list: energyProPlaceDesEnergiesList ?? [],
        saleDatabaseData: subscription?.sale_energy_pro_place_des_energies,
      },
      leadBankPanorabanques: {
        list: bankPanorabanquesList ?? [],
        saleDatabaseData: subscription?.sale_bank_panorabanques,
      },
      leadTelecom: {
        list: telecomList ?? [],
        saleDatabaseData: subscription?.sale_telecom,
      },
      leadTelecomLovys: {
        list: telecomLovysList ?? [],
        saleDatabaseData: subscription?.sale_telecom_lovys,
      },
      leadMonConseillerPerso: {
        list: monConseillerPersoList ?? [],
        saleDatabaseData: subscription?.sale_mon_conseiller_perso,
      },
      leadVerisure: {
        list: verisureList ?? [],
        saleDatabaseData: subscription?.sale_verisure,
      },
      leadGroupPurchaseTelecom: {
        list: groupPurchaseTelecomList ?? [],
        saleDatabaseData: subscription?.sale_group_purchase_telecom,
      },
      leadAssistancePro: {
        list: assistanceProList ?? [],
        saleDatabaseData: subscription?.sale_assistance_pro,
      },
      leadFloraAssuranceEthias: {
        list: floraAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_flora_assurance_ethias,
      },
      leadFloraAssuranceLead: {
        list: floraAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_flora_assurance_lead,
      },
      leadYagoAssuranceHouse: {
        list: yagoAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_yago_assurance_house,
      },
      leadYagoAssuranceCar: {
        list: yagoAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_yago_assurance_car,
      },
      leadYagoAssuranceMoto: {
        list: yagoAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_yago_assurance_moto,
      },
      leadSantevetAssurance: {
        list: santevetAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_sante_vet_assurance,
      },
      leadEthias: {
        list: ethiasList ?? [],
        saleDatabaseData: subscription?.sale_ethias,
      },
      leadElite: {
        list: eliteList ?? [],
        saleDatabaseData: subscription?.sale_elite,
      },
      leadEngieBrussels: {
        list: engieBrusselsList ?? [],
        saleDatabaseData: subscription?.sale_engie_brussels,
      },
      leadVerisureTelecomBe: {
        list: verisureTelecomBeList ?? [],
        saleDatabaseData: subscription?.sale_verisure_be,
      },
      leadBoilerEneco: {
        list: boilerCoverEnecoList ?? [],
        saleDatabaseData: subscription?.sale_boiler_eneco,
      },
      leadBoilerEngie: {
        list: boilerCoverEngieList ?? [],
        saleDatabaseData: subscription?.sale_boiler_engie,
      },
      leadBoilerHomeserve: {
        list: boilerCoverHomeserveList ?? [],
        saleDatabaseData: subscription?.sale_boiler_homeserve,
      },
      leadMesAllocs: {
        list: mesAllocsList ?? [],
        saleDatabaseData: subscription?.sale_administrative_service,
      },
      leadTucoenergie: {
        list: tucoenergieList ?? [],
        saleDatabaseData: subscription?.sale_tucoenergie,
      },
      leadEnergygo: {
        list: energygoList ?? [],
        saleDatabaseData: subscription?.sale_energygo,
      },
      leadGaranka: {
        list: boilerGarankaList ?? [],
        saleDatabaseData: subscription?.sale_boiler_garanka,
      },
      leadProxiserve: {
        list: boilerProxiserveList ?? [],
        saleDatabaseData: subscription?.sale_boiler_proxiserve,
      },
      leadBankinAssurance: {
        list: bankinAssuranceList ?? [],
        saleDatabaseData: subscription?.sale_bankin_assurance,
      },
      leadYouStock: {
        list: youStockList ?? [],
        saleDatabaseData: subscription?.sale_you_stock,
      },
      leadEddyProprietaire: {
        list: eddyProprietaireList ?? [],
        saleDatabaseData: subscription?.sale_eddy_proprietaire,
      },
      leadMovingNextories: {
        list: nextoriesList ?? [],
        saleDatabaseData: subscription?.sale_moving_nextories,
      },
      leadAssurancePro: {
        list: assuranceProList ?? [],
        saleDatabaseData: subscription?.sale_assurance_pro,
      },
      leadAccessoriesSfr: {
        list: accessoriesSfrList ?? [],
        saleDatabaseData: subscription?.sale_accessories_sfr,
      },
      leadVoltalisBizdev: {
        list: voltalisBizdevList ?? [],
        saleDatabaseData: subscription?.sale_voltalis_bizdev,
      },
      leadNeat: {
        list: neatList ?? [],
        saleDatabaseData: subscription?.sale_neat,
      },
      leadCentury21: {
        list: century21List ?? [],
        saleDatabaseData: subscription?.sale_century21,
      },
      leadAxaSmartphone: {
        list: axaSmartphoneList ?? [],
        saleDatabaseData: subscription?.sale_axa_smartphone,
      },
      leadHomyos: {
        list: homyosList ?? [],
        saleDatabaseData: subscription?.sale_homyos,
      },
      leadMutuelleSantiane: {
        list: mutuelleSantianeList ?? [],
        saleDatabaseData: subscription?.sale_mutuelle_santiane,
      },
      leadYolo: {
        list: yoloList ?? [],
        saleDatabaseData: subscription?.sale_yo_casa,
      },
      leadIren: {
        list: irenList ?? [],
        saleDatabaseData: subscription?.sale_ir,
      },
      leadTim: {
        list: timList ?? [],
        saleDatabaseData: subscription?.sale_ti_tim,
      },
      leadFastWeb: {
        list: fastWebList ?? [],
        saleDatabaseData: subscription?.sale_fw,
      },
      leadProtectedIt: {
        list: protectedItList ?? [],
        saleDatabaseData: subscription?.sale_protected_it,
      },
      leadEdison: {
        list: edisonList ?? [],
        saleDatabaseData: subscription?.sale_edison,
      },
      leadEnel: {
        list: enelList ?? [],
        saleDatabaseData: subscription?.sale_enel,
      },
      leadSorgenia: {
        list: sorgeniaList ?? [],
        saleDatabaseData: subscription?.sale_sorgenia,
      },
    },
  }
}

export default useAdditionalSaleFormData
